import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { SeoBreadcrumb } from "@/components/SeoBreadcrumb";
import { FullGallery } from "@/components/FullGallery";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Calendar, Trophy, Users, Star } from "lucide-react";

import childrenImage from "@/assets/children-upload-optimized.jpg";
import teensImage from "@/assets/teens-upload-optimized.jpg";
import adultsImage from "@/assets/adults-upload-optimized.jpg";
import beltCeremonyImage from "@/assets/eventi-upload-optimized.jpg";
import dojoImage from "@/assets/dojo-upload-optimized.jpg";
import galleryImage from "@/assets/gallery-upload-optimized.jpg";
import vitaImage from "@/assets/vita-upload-optimized.jpg";

const gallery = [
  { src: galleryImage, alt: "Allenamento al sacco", category: "Allenamento" },
  { src: beltCeremonyImage, alt: "Cerimonia delle cinture", category: "Eventi" },
  { src: childrenImage, alt: "Corso bambini", category: "Bambini" },
  { src: teensImage, alt: "Corso ragazzi", category: "Ragazzi" },
  { src: adultsImage, alt: "Corso adulti", category: "Adulti" },
  { src: dojoImage, alt: "La nostra palestra", category: "Palestra" },
];

const eventi = [
  {
    icon: Calendar,
    title: "Esami di cintura",
    description: "Due volte all'anno organizziamo gli esami per il passaggio di grado. Un momento importante vissuto in un clima sereno e di supporto reciproco."
  },
  {
    icon: Trophy,
    title: "Gare federali",
    description: "I nostri atleti partecipano a gare federali regionali e nazionali. La competizione è un momento di crescita e confronto."
  },
  {
    icon: Users,
    title: "Stage federali e seminari",
    description: "Stage federali, seminari tecnici e incontri con maestri di alto livello per approfondire la pratica e confrontarsi con altre realtà."
  },
  {
    icon: Star,
    title: "Eventi speciali",
    description: "Feste di fine anno, dimostrazioni pubbliche, eventi benefici: occasioni per stare insieme anche fuori dal tatami."
  }
];

const VitaInPalestra = () => {
  return (
    <Layout>
      <Helmet>
        <title>Vita in Palestra - Taekwondo Mattei Roma</title>
        <meta name="description" content="Scopri la vita in palestra: allenamenti, esami di cintura, gare federali, stage e momenti di crescita condivisa." />
        <link rel="canonical" href="https://taekwondomattei.com/vita-in-palestra" />
        <meta property="og:title" content="Vita in Palestra - Taekwondo Mattei Roma" />
        <meta property="og:description" content="Scopri la vita in palestra: allenamenti, esami di cintura, gare federali, stage e momenti di crescita condivisa." />
        <meta property="og:image" content="https://taekwondomattei.com/og-vita.jpg" />
        <meta property="og:url" content="https://taekwondomattei.com/vita-in-palestra" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Vita in Palestra - Taekwondo Mattei Roma" />
        <meta name="twitter:description" content="Scopri la vita in palestra: allenamenti, esami di cintura, gare federali, stage e momenti di crescita condivisa." />
        <meta name="twitter:image" content="https://taekwondomattei.com/og-vita.jpg" />
      </Helmet>
      <SeoBreadcrumb items={[{ label: "Vita in Palestra" }]} />
      {/* Hero */}
      <section className="py-24 md:py-32 bg-secondary">
        <div className="container-wide mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Vita in Palestra
            </h1>
            <p className="font-body text-xl text-muted-foreground">
              Durante l'anno la nostra palestra è animata da allenamenti ordinari, esami di cintura, 
              stage federali, gare, momenti formativi ed eventi interni. Ogni attività è pensata 
              per far crescere i nostri allievi dentro e fuori dal tatami.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="Momenti in palestra"
            subtitle="Allenamenti, eventi e vita quotidiana"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gallery.map((item, index) => (
              <div
                key={index}
                className="group relative aspect-[4/3] overflow-hidden rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300"
              >
                <img
                  src={item.src}
                  alt={item.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 hero-overlay-soft pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-t from-black-deep/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <span className="font-body text-xs font-medium text-gold/80 uppercase tracking-wider">
                      {item.category}
                    </span>
                    <h3 className="font-display text-lg font-semibold text-gold mt-1">
                      {item.alt}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Gallery */}
      <FullGallery />

      {/* Eventi */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <SectionHeader
            title="Eventi e appuntamenti"
            subtitle="I momenti speciali che scandiscono l'anno"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {eventi.map((evento) => (
              <div key={evento.title} className="card-elevated p-8 border border-border">
                <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-6">
                  <evento.icon className="h-6 w-6 text-gold" />
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                  {evento.title}
                </h3>
                <p className="font-body text-muted-foreground leading-relaxed">
                  {evento.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community */}
      <section className="section-padding bg-background">
        <div className="container-wide mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeader
                title="Una comunità che cresce"
                subtitle=""
                centered={false}
              />
              <div className="space-y-6 font-body text-lg text-muted-foreground leading-relaxed">
                <p>
                  Nella nostra palestra si respira un'atmosfera familiare. Genitori che 
                  aspettano i figli, amicizie che nascono tra gli allievi, supporto 
                  reciproco durante le sfide.
                </p>
                <p>
                  Crediamo che il Taekwondo sia più di uno sport: è un modo di vivere, 
                  un percorso che coinvolge tutta la famiglia. Per questo organizziamo 
                  momenti di condivisione anche fuori dagli allenamenti.
                </p>
                <p>
                  Che tu sia un bambino alle prime armi, un adolescente in cerca di 
                  stimoli o un adulto che vuole rimettersi in forma, qui troverai 
                  un ambiente accogliente e rispettoso.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <img
                src={childrenImage}
                alt="Corso bambini"
                className="rounded-xl shadow-soft"
                loading="lazy"
              />
              <img
                src={teensImage}
                alt="Corso ragazzi"
                className="rounded-xl shadow-soft mt-8"
                loading="lazy"
              />
              <img
                src={adultsImage}
                alt="Corso adulti"
                className="rounded-xl shadow-soft -mt-4"
                loading="lazy"
              />
              <img
                src={beltCeremonyImage}
                alt="Cerimonia cinture"
                className="rounded-xl shadow-soft mt-4"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding gradient-black">
        <div className="container-narrow mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gold mb-6">
            Entra a far parte della nostra comunità
          </h2>
          <p className="font-body text-lg text-gold/70 mb-8 max-w-xl mx-auto">
            La prima lezione è gratuita. Vieni a conoscere i maestri, gli allievi 
            e l'atmosfera della nostra palestra.
          </p>
          <Link to="/lezione-di-prova" className="btn-accent text-lg">
            Prenota una lezione di prova
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default VitaInPalestra;
